import { buildQuery, CombinedFilter, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { ExportStoreService } from '../../export-overlay.store';
import { ExportRange, ExportFileFormat, ExportTaskStatus } from './export-task.enum';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { mergeMap, Observable, of, Subscription, timer } from 'rxjs';
import { ExcelGenerator } from './generators/excel-generator';
import { JSONGenerator } from './generators/json-generator';
import { ExportTaskProcessor } from './processors/export-task-processor';
import { CSVGenerator } from './generators/csv-generator';
import { ExportTaskInternalShared } from './export-task-internal.interfaces';

const fileFormatsForDownload = {
  [ExportFileFormat.Excel]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  [ExportFileFormat.CSV]: 'text/csv',
  [ExportFileFormat.JSON]: 'application/json'
}

export abstract class LegacyExportTask {
  private internalTitle: string;
  public readonly uuid = crypto.randomUUID();
  private sub: Subscription;
  private pageSize = 0;
  status = ExportTaskStatus.Busy;
  currentPage = 0;
  totalPages = 0;

  get title() {
    return this.internalTitle;
  }

  get sections() {
    return this.processor.sections;
  }

  get iterations() {
    return this.totalPages;
  }

  get iteration() {
    return this.currentPage;
  }

  private internalShared: ExportTaskInternalShared;

  constructor(
    public readonly id: string,
    title = id,
    public readonly itemCount = 0,
    protected readonly range: ExportRange,
    protected readonly format: ExportFileFormat,
    private initialFilter: CombinedFilter<any> | null | undefined,
    private store: ExportStoreService,
    protected processor = new ExportTaskProcessor(),
  ) {
    this.internalTitle = title;
    this.internalShared = {
      id: this.id,
      setTitle: (titleChange: string) => {
        this.internalTitle = titleChange;
      },
      getTitle: () => {
        return this.internalTitle;
      },
      setIterations: (value: number) => {
        this.totalPages = value;
      },
      setIteration: (value: number) => {
        this.currentPage = value;
      },
      getIteration: () => {
        return this.currentPage;
      },
      isStopped: () => {
        return this.status !== ExportTaskStatus.Busy;
      },
    };
  }

  downloadFile(filename: string, content: any, format: ExportFileFormat) {
    /** Possible fix to export legacy task */
    //if (this.status !== ExportTaskStatus.Busy) return;
    const a = document.createElement('a');
    const blob = new Blob([content], { type: get(fileFormatsForDownload, format) });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  private generateJSONExportContent() {
    const generator = new JSONGenerator(this.sections);
    return JSON.stringify(generator.generateObject());
  }

  private async generateExcelExportContent() {
    const generator = new ExcelGenerator(this.sections, this.store, this.internalShared);
    const wb = await generator.generateWorkBook();
    return wb.xlsx.writeBuffer();
  }

  private generateCSVExportContent() {
    const generator = new CSVGenerator(this.sections);
    return generator.generateDocuments();
  }

  /** ⚠️ Returning the results at the onRequest pipeline is discouraged.
   * Please use processors, these can handle data more efficiently.
   * @example this.processor.addData(item)
   * @example this.processor.addToSection('Detalles', item) */
  private resolveLinearOnRequestResult(result: Array<any>) {
    if (typeof result === 'undefined' || result === null || !Array.isArray(result) ) {
      return;
    }
    result.forEach((item) => this.addToSection(
      this.format === ExportFileFormat.JSON ? null : this.title,
      item,
    ));
  }

  private doRequest() {
    return this.onRequest(buildQuery(
      seed(this.initialFilter), page(this.currentPage, this.pageSize)
    )).pipe(mergeMap((result) => {
      this.resolveLinearOnRequestResult(result);
      this.store.emitChange();
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
        // The pipeline gets in loop until the current page reaches the last page
        return this.doRequest();
      }
      return of(null);
    }))
  }

  protected createNewSection(name: string, title: string) {
    this.processor.setSectionTitle(name, title);
  }

  protected setSectionTitle(name: string, title: string) {
    this.processor.setSectionTitle(name, title);
  }

  protected addToSection(name: string, item: any | any[]) {
    this.processor.addToSection(name, item);
  }

  protected localizeItemProperties(sectionName: string, properties: Record<string, string>) {
    this.processor.localizeItemProperties(sectionName, properties);
  }

  start() {
    this.store.addTask(this);
    this.pageSize = get(this.initialFilter, 'page.size', 1);
    if (this.range === ExportRange.AllPages) {
      this.currentPage = 1;
      this.totalPages = ceil(this.itemCount / get(this.initialFilter, 'page.size', 0)) + 1;
    } else {
      this.currentPage = get(this.initialFilter, 'page.pageIndex', 1);
      this.totalPages = 1;
    }
    this.processor.onInit();
    Promise.resolve(this.onStart()).then(() => {
      this.sub = this.doRequest().subscribe(async () => {
        this.status = ExportTaskStatus.Complete;
        this.store.emitChange();
        this.store.setVisible(true);
        timer(4500).subscribe(() => { this.store.removeTask(this.id); });
  
        if (this.format === ExportFileFormat.Excel) {
          const excelContent = await this.generateExcelExportContent();
          this.downloadFile(`Export_${this.id}.xlsx`, excelContent, this.format);
        } else if (this.format === ExportFileFormat.CSV) {
          const documents = this.generateCSVExportContent();
          documents.forEach((doc) => {
            const titleParts = ['Export', this.id];
            if (doc.title && documents.length > 1) titleParts.push(doc.title);
            this.downloadFile(`${titleParts.join('_')}.csv`, doc.content, this.format);
          });
        } else {
          this.downloadFile(`Export_${this.id}.json`, this.generateJSONExportContent(), this.format);
        }
      });
    });
    
  }

  cancel() {
    if (this.sub) this.sub.unsubscribe();
    this.status = ExportTaskStatus.Canceled;
    this.store.emitChange();
    timer(4500).subscribe(() => { this.store.removeTask(this.id); });
  }

  /** Called before the onRequest method. */
  onStart(): void | Promise<void> {}

  /** Send requests, process responses and transform data here. */
  abstract onRequest(currentFilter: CombinedFilter<any>): Observable<any>;
}
